import { ISecurityRequirementPaymentInfo } from '@carm/_core/financial-security/models/financial-security-api.model';
import { IFinancialSecurityEnrolment } from '@carm/_core/financial-security/models/financial-security.model';
import { ProgramTypes } from '@carm/_core';
import { IEdiDraftRequest, IProgramEnrolmentDraftResponse, IProgramEnrolmentSaveMediaRequest } from '@carm/_core/program-enrolment/models/enrol-program-api.model';
import { CarrierSubProgramType, CheckedOptions, EdiServiceProviderCategoryType, EnrolmentType,
    ProgramEnrolmentType, SubProgramEnrolmentType, thirdPartyTypeToS4EnrolmentTypesMap, WarehouseSubProgramType } from '@carm/_core/program-enrolment/models/enrol-program-enums.model';
import { IEdiServiceEnrolmentDetails } from '@carm/_core/program-enrolment/models/enrol-program.model';
import { mapProgramTypeToProgramEnrolmentType } from '@carm/_core/business-registration/converters';

export interface IProgramEnrolmentInfo {
    programAccountName: string;
    programAccountNumber: string;
    programType: ProgramTypes;
    programSubType: WarehouseSubProgramType | CarrierSubProgramType;
    securityRequirementInfo?: ISecurityRequirementPaymentInfo;
    isSubProgram: boolean;
    isRppBonded?: boolean;
    isRppRevoked?: boolean;
    isRppSuspended?: boolean;
    isBondedStatus?: boolean;
    enrolledSubPrograms?: string[];
    inProgressSubPrograms?: string[];
    isCommercialImporter?: boolean;
}

export interface ISubProgramRequirements extends IEdiServiceEnrolmentDetails {
    enrolmentType?: EnrolmentType;
    region?: string;
    oneTimeImportation?: string;
}

export interface ISubProgramEnrolment {
    draftId?: string;
    caseNumber?: string;
    program: IProgramEnrolmentInfo;
    programAccountName?: string;
    subProgramType?: SubProgramEnrolmentType;
    securityRequirementType?: SecurityRequirementsType;
    requirements?: ISubProgramRequirements;
    draft?: IProgramEnrolmentDraftResponse;
}

export interface IFinancialSecurityEnrolmentRequest extends IProgramEnrolmentSaveMediaRequest {
    certification?: boolean;
    programAccountName?: string;
    subType?: SubProgramEnrolmentType;
    edi?: IEdiDraftRequest;
    region?: string;
    oneTimeImportation?: string;
}

export enum SecurityRequirementsType {
    ReleasePriortoPayment = 'rpp',
    Broker = 'broker',
    DutyFreeShop = 'dfs',
    SufferanceWarehouse = 'sw',
    CustomsBondedWarehouse = 'bw',
    AirTransporter = 'at',
    FreightForwarderTransporter = 'ff',
    MarineTransporter = 'mt',
    RailTransporter = 'rt',
    HighwayTransporter = 'hwt',
}

export const getSecurityRequirementTypeBy = (programType: ProgramTypes, programSubType: WarehouseSubProgramType | CarrierSubProgramType | undefined):
                                            SecurityRequirementsType => {
    const securityRequirementsMapping = new Map<string, SecurityRequirementsType>();
    securityRequirementsMapping[ProgramTypes.Importer] = SecurityRequirementsType.ReleasePriortoPayment;
    securityRequirementsMapping[ProgramTypes.ImporterR1] = SecurityRequirementsType.ReleasePriortoPayment;
    securityRequirementsMapping[ProgramTypes.CustomsBroker] = SecurityRequirementsType.Broker;
    securityRequirementsMapping[ProgramTypes.DutyFreeShop] = SecurityRequirementsType.DutyFreeShop;
    securityRequirementsMapping[`${ProgramTypes.Warehouse}_${WarehouseSubProgramType.SufferanceWarehouse}`] = SecurityRequirementsType.SufferanceWarehouse;
    securityRequirementsMapping[`${ProgramTypes.Warehouse}_${WarehouseSubProgramType.CustomsBondedWarehouse}`] =
                                                                                                    SecurityRequirementsType.CustomsBondedWarehouse;
    securityRequirementsMapping[`${ProgramTypes.Carriers}_${CarrierSubProgramType.Air}`] = SecurityRequirementsType.AirTransporter;
    securityRequirementsMapping[`${ProgramTypes.Carriers}_${CarrierSubProgramType.CarrierFreightForwarder}`] =
                                                                                                    SecurityRequirementsType.FreightForwarderTransporter;
    securityRequirementsMapping[`${ProgramTypes.Carriers}_${CarrierSubProgramType.Highway}`] = SecurityRequirementsType.HighwayTransporter;
    securityRequirementsMapping[`${ProgramTypes.Carriers}_${CarrierSubProgramType.Marine}`] = SecurityRequirementsType.MarineTransporter;
    securityRequirementsMapping[`${ProgramTypes.Carriers}_${CarrierSubProgramType.Rail}`] = SecurityRequirementsType.RailTransporter;

    if ([ProgramTypes.Importer, ProgramTypes.ImporterR1, ProgramTypes.CustomsBroker, ProgramTypes.DutyFreeShop].includes(programType)) {
        return securityRequirementsMapping[programType];
    }
    return securityRequirementsMapping[`${programType}${programSubType ? '_' + programSubType : ''}`];
};

export const mapToFinancialEnrolmentRequest = (model: IFinancialSecurityEnrolment, isNewEnrolment?: boolean): IFinancialSecurityEnrolmentRequest => {
    const carrierType = model.program?.programType === ProgramTypes.Carriers ? model.program?.programSubType : undefined;
    const warehouseType = model.program?.programType === ProgramTypes.Warehouse ? model.program?.programSubType : undefined;
    return {
        draftId: model.draft?.draftId || model.draftId,
        programType: model.program?.isSubProgram ? ProgramEnrolmentType.SubProgram : mapProgramTypeToProgramEnrolmentType(model.program?.programType),
        certification: true,
        enrolmentType: model.requirements?.enrolmentType ?? (isNewEnrolment === true ?
                       EnrolmentType.New : (model.requirements?.enrolmentType ?? EnrolmentType.New)),
        region: model.requirements?.region,
        oneTimeImportation: model.requirements?.oneTimeImportation
            ? CheckedOptions.Checked
            : CheckedOptions.UnChecked,
        programAccountName: model.program?.programAccountName,
        subType: model.isBondedApply ? ProgramEnrolmentType.Bonded : model.subProgramType,
        edi: model.subProgramType === SubProgramEnrolmentType.Edi
            ? {
                ediConnection: model.requirements?.ediServiceProviderCategories?.includes(
                    EdiServiceProviderCategoryType.EdiConnection
                )
                    ? CheckedOptions.Checked
                    : CheckedOptions.UnChecked,
                ediSoftware: model.requirements?.ediServiceProviderCategories?.includes(
                    EdiServiceProviderCategoryType.EdiSoftware
                )
                    ? CheckedOptions.Checked
                    : CheckedOptions.UnChecked,
                ediConnectionMethod: model.requirements?.connectionType,
                requestNewConnection: model.requirements?.requestConnectionType
                    ? CheckedOptions.Checked
                    : CheckedOptions.UnChecked,
                ediService: !!model.requirements?.ediServiceType
                    ? thirdPartyTypeToS4EnrolmentTypesMap.get(model.requirements?.ediServiceType)
                    : undefined
              } as IEdiDraftRequest
            : undefined,
        carrierType,
        warehouseType
    } as IFinancialSecurityEnrolmentRequest;
};
